import { API_URL } from './config'
import { getToken, refreshSession, endSession } from './auth'

// FastAPI validation errors (422) return detail as an array of
// {loc, msg, type} objects — flatten them into a readable message.
export function formatDetail(detail) {
  if (Array.isArray(detail)) {
    return detail.map(d => {
      if (d && typeof d === 'object') {
        const loc = Array.isArray(d.loc) ? d.loc.filter(p => p !== 'body').join('.') : ''
        const msg = d.msg ?? JSON.stringify(d)
        return loc ? `${loc}: ${msg}` : msg
      }
      return String(d)
    }).join('; ')
  }
  if (detail && typeof detail === 'object') return JSON.stringify(detail)
  return detail
}

async function authFetch(path, { signal, ...options } = {}) {
  // The token is read per attempt, not once: the retry below must pick up the
  // freshly minted one rather than replay the expired header.
  const doFetch = () => {
    const token = getToken()
    return fetch(`${API_URL}${path}`, {
      ...options,
      signal,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...options.headers,
      },
    })
  }

  let res = await doFetch()
  // A 401 usually just means the 1-hour access token expired mid-work. Renew it
  // silently from the httpOnly refresh cookie and retry once — only a 401 that
  // survives that means the session is really gone and sign-in is required.
  if (res.status === 401) {
    if (await refreshSession()) res = await doFetch()
    if (res.status === 401) {
      endSession({ reason: 'session-expired' })
      throw new Error('Session expired')
    }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(formatDetail(err.detail) ?? 'Request failed')
  }
  if (res.status === 204) return null
  return res.json()
}

export function buildQuery(params) {
  // Array values become repeated params: { capabilities: ['a','b'] } -> ?capabilities=a&capabilities=b
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v == null || v === '') continue
    if (Array.isArray(v)) v.forEach(item => { if (item != null && item !== '') sp.append(k, item) })
    else sp.append(k, v)
  }
  const s = sp.toString()
  return s ? '?' + s : ''
}

export const api = {
  getDevices: (params = {}, opts = {}) => authFetch(`/devices${buildQuery(params)}`, opts),
  getDevice: (id, opts = {}) => authFetch(`/devices/${id}`, opts),
  // Fetch this device's cloud blob from Emporia Cloud now (skips the 5-min sync).
  syncDevice: (id) => authFetch(`/devices/${id}/sync`, { method: 'POST' }),
  updateDevice: (id, body) => authFetch(`/devices/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
  createDevice: (body) => authFetch('/devices', { method: 'POST', body: JSON.stringify(body) }),
  deleteDevice: (id) => authFetch(`/devices/${id}`, { method: 'DELETE' }),
  getDeviceCapabilities: (id, opts = {}) => authFetch(`/devices/${id}/capabilities`, opts),
  getTimeZoneChanges: (id, opts = {}) => authFetch(`/devices/${id}/time_zone_changes`, opts),
  getAccounts: (params = {}, opts = {}) => authFetch(`/accounts${buildQuery(params)}`, opts),
  getAccount: (id, opts = {}) => authFetch(`/accounts/${id}`, opts),
  // Deliberately separate from getAccount: the API keeps password reads explicit.
  getAccountCredentials: (id) => authFetch(`/accounts/${id}/credentials`),
  createAccount: (body) => authFetch('/accounts', { method: 'POST', body: JSON.stringify(body) }),
  deleteAccount: (id) => authFetch(`/accounts/${id}`, { method: 'DELETE' }),
  getReservations: ({ includeAll = false } = {}, opts = {}) =>
    authFetch(`/reservations${includeAll ? '?include_all=true' : ''}`, opts),
  createReservation: (reservable_id, reservable_type) =>
    authFetch('/reservations', {
      method: 'POST',
      body: JSON.stringify({ reservable_id, reservable_type }),
    }),
  cancelReservation: (id) => authFetch(`/reservations/${id}`, { method: 'DELETE' }),
  createBundle: (body) => authFetch('/bundle', { method: 'POST', body: JSON.stringify(body) }),
  // Devices onto an Emporia account the inventory doesn't track (e.g. your own login).
  createExternalBundle: (body) => authFetch('/bundle/external', { method: 'POST', body: JSON.stringify(body) }),
  getBundles: (opts = {}) => authFetch('/bundle', opts),
  getBundle: (id, opts = {}) => authFetch(`/bundle/${id}`, opts),
  cancelBundle: (id) => authFetch(`/bundle/${id}`, { method: 'DELETE' }),

  // Capabilities & constraints
  getCapabilities: (opts = {}) => authFetch('/capabilities', opts),
  getConstraints: (deviceId, opts = {}) =>
    authFetch(`/constraints${buildQuery({ device_id: deviceId })}`, opts),
  createConstraint: (body) => authFetch('/constraints', { method: 'POST', body: JSON.stringify(body) }),
  deleteConstraint: (deviceId, capabilityId) =>
    authFetch(`/constraints/${deviceId}/${capabilityId}`, { method: 'DELETE' }),
}
